import React, { useEffect, useState } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import { FaTrashAlt } from "react-icons/fa";
import axios from "axios";
import { apiKey } from "../../../config/config";
import { generateReferenceNumber } from "../../../config/helperFunctions";
import { toast } from "react-toastify";
import MessageCard from "../../../components/AdminCards/MessageCard";

function AddInventoryAdjForm() {
  const { id } = useParams();
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    reference_no: generateReferenceNumber(),
    date: new Date().toISOString().split("T")[0],
    employee_id: null,
    ingredients: [],
    note: "",
    user_id: user?.user.id,
    outlet_id: user?.user.outlet_id,
  });
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [data, setData] = useState([]);
  const dataOption = [
    { value: "plus", label: "Plus" },
    { value: "minus", label: "Minus" },
  ];
  const [personOption, setPersonOption] = useState([]);

  const navigate = useNavigate();

  const handleChange = (e) =>
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

  const handleIngredientSelect = (e) => {
    setSelectedIngredients([
      ...selectedIngredients,
      {
        ingredient_id: e.value,
        name: e.ingredient.name,
        code: e.ingredient.code,
        qty: "",
        price: e.ingredient.purchase_price,
        unit: e.ingredient.purchase_ingredient_unit?.unit_name || "",
        unitValue: e.ingredient.purchase_ingredient_unit?.unit_value || "",
        vat_percentage: e.ingredient.vat_percentage,
        tax_method: e.ingredient.tax_method,
        vat: e.ingredient.ing_vat,
        total: "",
      },
    ]);
  };

  const handleDelete = (index) => {
    setSelectedIngredients((prevIngredients) =>
      prevIngredients.filter((_, i) => i !== index)
    );
  };

  const qtyChange = (index, value) => {
    const updatedIngredients = [...selectedIngredients];
    updatedIngredients[index].qty = value;
    setSelectedIngredients(updatedIngredients);
  };

  const handleConsumptionStatusChange = (index, selectedOption) => {
    const updatedIngredients = [...selectedIngredients];
    updatedIngredients[index].consumptionStatus = selectedOption;
    setSelectedIngredients(updatedIngredients);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      ingredients: selectedIngredients.map((ingredient) => ({
        ingredient_id: ingredient.ingredient_id,
        qty: ingredient.qty,
        consumption_status: ingredient.consumptionStatus,
      })),
    };

    if (id) {
      axios
        .put(`${apiKey}/api/rms/admin/inventoryadjustment/${id}`, payload, {
          headers: { Authorization: `Bearer ${user.token}` },
        })
        .then((response) => {
          toast.success("Success", {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
          navigate(-1);
        })
        .catch((err) => {
          Object.keys(err.response.data.data).forEach((item) => {
            err.response.data.data[item].forEach((i) => {
              toast.error(i || "Validation Error");
            });
          });
        });
    } else {
      axios
        .post(`${apiKey}/api/rms/admin/inventoryadjustment`, payload, {
          headers: { Authorization: `Bearer ${user.token}` },
        })
        .then((response) => {
          toast.success("Success", {
            position: "bottom-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          });
          navigate(-1);
        })
        .catch((err) => {
          Object.keys(err.response.data.data).forEach((item) => {
            err.response.data.data[item].forEach((i) => {
              toast.error(i || "Validation Error");
            });
          });
        });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, ingredientsRes] = await Promise.all([
          axios.get(`${apiKey}/api/rms/admin/user`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
          axios.get(`${apiKey}/api/rms/admin/ingredients`, {
            headers: { Authorization: `Bearer ${user.token}` },
          }),
        ]);

        // users
        setPersonOption(
          usersRes.data.map((u) => ({ label: u.name, value: u.id }))
        );

        // ingredients
        setData(
          ingredientsRes.data.map((ingredient) => ({
            label: `${ingredient.name}(${ingredient.code})`,
            value: ingredient.id,
            ingredient: ingredient,
          }))
        );
      } catch (err) {
        setError(err.response?.data?.message || "Error Occured");
      }
    };

    fetchData();

    if (id) {
      setPageLoading(true);
      axios
        .get(`${apiKey}/api/rms/admin/inventoryadjustment/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` },
        })
        .then((response) => {
          const adj = response.data;
          setFormData({
            reference_no: String(adj.reference_no),
            date: adj.date,
            employee_id: adj.employee_id,
            responsible_person: adj.responsible_person,
            note: adj.note,
            user_id: adj.user_id,
            outlet_id: adj.outlet_id,
          });
          setSelectedIngredients(
            adj.items.map((ingredient) => ({
              ingredient_id: ingredient.ingredient.id,
              qty: ingredient.qty,
              consumptionStatus: ingredient.consumption_status,
              name: ingredient.ingredient.name,
              code: ingredient.ingredient.code,
              unit: ingredient.ingredient.ingredient_unit?.unit_name || "",
            }))
          );
        })
        .catch((err) => {
          setError(err.response?.data?.message || "Error Occured");
        })
        .finally(() => {
          setPageLoading(false);
        });
    }
  }, [id, user.token]);

  if (pageLoading) {
    return (
      <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
        <img src="/assets/gif/dash2.gif" alt="" />
      </div>
    );
  }

  if (error) {
    return <MessageCard error={error} />;
  }

  return (
    <main>
      <div className="relative my-16 flex flex-col min-w-0 break-words w-full mb-6 shadow-lg bg-white rounded-lg p-4 border-0">
        <div className="flex justify-between items-center mb-4">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-d-bg-text text-2xl">
              Add Inventory Adjustment
            </h1>
          </div>
        </div>
        <div className="w-full">
          <form action="" className="w-full" onSubmit={handleSubmit}>
            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 font-bold">
                  Reference No
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="text"
                    placeholder="0000007"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full bg-gray-100"
                    required
                    onChange={handleChange}
                    name={"reference_no"}
                    value={formData.reference_no}
                    disabled
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2 font-bold">
                  Date
                </span>
                <div className="mb-2 pt-0">
                  <input
                    type="date"
                    placeholder="Date"
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-gray-100 rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    required
                    name={"date"}
                    value={formData.date}
                    onChange={handleChange}
                    disabled
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 relative before:content-['*'] before:text-red-500 before:absolute before:top-0 before:right-0 before:translate-x-2 font-bold">
                  Responsible Person
                </span>
                <div className="mb-2 ">
                  <Select
                    type="text"
                    className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full  "
                    placeholder="Select"
                    name={"employee_id"}
                    options={personOption}
                    onChange={(e) => {
                      setFormData({ ...formData, employee_id: e.value });
                    }}
                    defaultValue={{
                      label: formData.responsible_person?.name,
                      value: formData.employee_id,
                    }}
                  />
                </div>
              </label>
            </div>

            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 font-bold">
                  Ingredients
                </span>
                <div className="mb-2 pt-0">
                  <Select
                    type="text"
                    className="placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    placeholder="Select"
                    options={data}
                    onChange={handleIngredientSelect}
                    closeMenuOnSelect={false}
                  />
                </div>
              </label>

              <label htmlFor="" className="w-full md:w-1/3"></label>

              <label htmlFor="" className="w-full md:w-1/3"></label>
            </div>

            <table className="w-full">
              <thead className="border-b mb-2">
                <tr>
                  <th className="text-left py-2 text-xs">SN</th>
                  <th className="text-left py-2 text-xs">Ingredient(Code)</th>
                  <th className="text-left py-2 text-xs">Quantity/Amount</th>
                  <th className="text-left py-2 text-xs">Consumption Status</th>
                  <th className="text-left py-2 text-xs">Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedIngredients.map((ingredient, index) => (
                  <tr key={index} className="">
                    <td className="text-left py-2 text-xs">{index + 1}</td>
                    <td className="text-left py-2 text-xs">
                      {ingredient.name}({ingredient.code})
                    </td>
                    <td className="text-left py-2 text-xs">
                      <input
                        type="text"
                        value={ingredient.qty}
                        className="border rounded w-20 px-1 py-0.5"
                        onChange={(e) => qtyChange(index, e.target.value)}
                      />{" "}
                      {ingredient.unit}
                    </td>
                    <td className="text-left py-2 text-xs">
                      <Select
                        options={dataOption}
                        className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-60"
                        onChange={(e) =>
                          handleConsumptionStatusChange(index, e.value)
                        }
                        defaultValue={{
                          label:
                            ingredient.consumptionStatus === "plus"
                              ? "Plus"
                              : ingredient.consumptionStatus === "minus"
                              ? "Minus"
                              : "Select",
                          value: ingredient.consumptionStatus,
                        }}
                      />
                    </td>
                    <td className="text-left py-2 text-xs">
                      <button
                        onClick={() => handleDelete(index)}
                        type="button"
                        className="border border-d-bg] text-d-bg-text size-6 grid place-items-center rounded-full"
                      >
                        <FaTrashAlt />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex flex-col md:flex-row gap-x-8 gap-y-2 w-full ">
              <label htmlFor="" className="w-full md:w-1/3">
                <span className="text-d-bg-text text-sm mb-2 font-bold">
                  Note
                </span>
                <div className="mb-2 pt-0">
                  <textarea
                    rows="4"
                    type="text"
                    placeholder="Note"
                    name={"note"}
                    className="px-2 py-2 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:shadow-outline w-full"
                    onChange={handleChange}
                  />
                </div>
              </label>
            </div>

            <span className="w-full bg-d-bg h-1 my-2 inline-block"></span>
            <div className="flex gap-2">
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit"
                type="submit"
              >
                Submit
              </button>
              <button
                className="bg-d-cat-btn text-white text-sm px-3 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center w-fit"
                type="button"
                onClick={() => navigate(-1)}
              >
                Back
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}

export default AddInventoryAdjForm;
